import {
  ArrowBack as BackIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  EditNote as EditIcon,
  MenuBook as GuidesIcon,
  OpenInNew as OpenIcon,
  Publish as PublishIcon,
  RateReview as RequestChangesIcon,
  Unpublished as UnpublishIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import GuideFactCheckFindings from '../components/GuideFactCheckFindings';
import { titleText } from '../components/guide-editor/model';
import { guidePreviewUrl } from '../components/guide-editor/urls';
import { useAuth } from '../contexts/GoogleAuthContext';
import {
  api,
  GUIDE_REVIEW_REASON_LABELS,
  GUIDE_REVIEW_REASONS,
  GuideDetail as GuideDetailType,
  GuideGateStatus,
  GuidePublishGates,
  GuideReviewDecision,
  GuideReviewHistoryResponse,
  GuideReviewReason,
} from '../services/api-client';

const GATE_STATUS_COLORS: Record<GuideGateStatus, 'success' | 'error' | 'warning'> = {
  pass: 'success',
  fail: 'error',
  advisory: 'warning',
};

const GATE_STATUS_LABELS: Record<GuideGateStatus, string> = {
  pass: 'Pass',
  fail: 'Fail',
  advisory: 'Advisory',
};

const VERDICT_COLORS: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  publish: 'success',
  fix: 'warning',
  kill: 'error',
};

export default function GuideDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();

  const [guide, setGuide] = useState<GuideDetailType | null>(null);
  const [reviewInfo, setReviewInfo] = useState<GuideReviewHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [dialogDecision, setDialogDecision] = useState<GuideReviewDecision | null>(null);
  const [selectedReasons, setSelectedReasons] = useState<GuideReviewReason[]>([]);
  const [note, setNote] = useState('');

  // Publish gate (G3)
  const [gates, setGates] = useState<GuidePublishGates | null>(null);
  const [gatesError, setGatesError] = useState<string | null>(null);
  const [confirmSuperlative, setConfirmSuperlative] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [unpublishOpen, setUnpublishOpen] = useState(false);
  const [unpublishReason, setUnpublishReason] = useState('');
  const [blockedGates, setBlockedGates] = useState<{ gate: string; detail: string }[] | null>(null);

  const canReview = user?.role === 'admin' || user?.effective_permissions?.content?.publish;

  // The checklist is evaluated by the Content API for the signed-in reviewer
  // (mapped server-side) and the current superlative confirmation, so what the
  // button says is what a publish request would do.
  const fetchGates = async (confirm: boolean) => {
    if (!slug) return;
    try {
      setGates(await api.getGuidePublishGates(slug, { confirmSuperlativeTitle: confirm }));
      setGatesError(null);
    } catch (err: any) {
      setGates(null);
      setGatesError(err.message || 'Failed to load the publish checklist');
    }
  };

  const fetchData = async (confirm: boolean = confirmSuperlative) => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const [guideData, reviewData] = await Promise.all([
        api.getGuide(slug),
        api.getGuideReview(slug).catch(() => ({ review: null, review_history: [] })),
        fetchGates(confirm),
      ]);
      setGuide(guideData);
      setReviewInfo(reviewData);
    } catch (err: any) {
      setError(err.message || 'Failed to load guide');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setConfirmSuperlative(false);
    fetchData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const toggleSuperlative = async (checked: boolean) => {
    setConfirmSuperlative(checked);
    await fetchGates(checked);
  };

  const handlePublish = async () => {
    if (!slug) return;
    setActionLoading(true);
    setError(null);
    setBlockedGates(null);
    try {
      await api.publishGuide(slug, { confirmSuperlativeTitle: confirmSuperlative });
      setPublishOpen(false);
      setConfirmSuperlative(false);
      await fetchData(false);
    } catch (err: any) {
      setPublishOpen(false);
      const failed = err?.details?.failed;
      if (err?.status === 409 && Array.isArray(failed)) {
        // A gate moved between the checklist and the click: show which, and
        // refresh so the checklist matches.
        setBlockedGates(failed);
        await fetchGates(confirmSuperlative);
      } else {
        setError(err.message || 'Failed to publish');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnpublish = async () => {
    if (!slug || !unpublishReason.trim()) return;
    setActionLoading(true);
    setError(null);
    try {
      await api.unpublishGuide(slug, { reason: unpublishReason.trim() });
      setUnpublishOpen(false);
      setUnpublishReason('');
      await fetchData(false);
    } catch (err: any) {
      setUnpublishOpen(false);
      setError(err.message || 'Failed to unpublish');
    } finally {
      setActionLoading(false);
    }
  };

  const openDialog = (decision: GuideReviewDecision) => {
    setSelectedReasons([]);
    setNote('');
    setDialogDecision(decision);
  };

  const closeDialog = () => {
    if (actionLoading) return;
    setDialogDecision(null);
  };

  const toggleReason = (reason: GuideReviewReason) => {
    setSelectedReasons((prev) =>
      prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]
    );
  };

  const requiresNote = selectedReasons.includes('other');
  const canSubmitDialog = selectedReasons.length > 0 && (!requiresNote || note.trim().length > 0);

  const submitReview = async (decision: GuideReviewDecision, reasons: GuideReviewReason[], noteValue?: string) => {
    if (!slug) return;
    setActionLoading(true);
    setError(null);
    try {
      await api.reviewGuide(slug, {
        decision,
        reasons,
        note: noteValue?.trim() || undefined,
      });
      setDialogDecision(null);
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to submit review');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = () => submitReview('approve', []);

  const handleDialogSubmit = () => {
    if (!dialogDecision) return;
    submitReview(dialogDecision, selectedReasons, note);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !guide) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/guides')} sx={{ mb: 2 }}>
          Back to Guides
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!guide) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/guides')} sx={{ mb: 2 }}>
          Back to Guides
        </Button>
        <Alert severity="warning">Guide not found</Alert>
      </Box>
    );
  }

  // A block guide's title is `{text, accent}`; a flat article's is a string.
  const title = titleText(guide.title) || guide.name || slug || '';
  const qaReport = guide.qa?.report;
  const judge = guide.qa?.judge;
  const currentReview = reviewInfo?.review;
  const history = reviewInfo?.review_history || [];
  const isPublished = gates ? gates.status === 'published' : !guide.draft;
  const myReviewer = gates?.me.reviewer ?? null;
  // Why Publish is disabled, in the order a person would fix it.
  const publishBlocker: string | null = !gates
    ? gatesError || 'The publish checklist is not available.'
    : !myReviewer
    ? `Only an editorial reviewer can publish (${gates.reviewers.join(', ')}). Your account is not mapped to one.`
    : gates.canPublish
    ? null
    : gates.failed.map((f) => f.detail).join(' · ');

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate('/guides')}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Chip
              icon={<GuidesIcon />}
              label={guide.draft ? 'Draft' : guide.status || 'Published'}
              color={guide.draft ? 'default' : 'success'}
              size="small"
            />
            {currentReview && (
              <Chip
                label={`Last review: ${currentReview.decision}`}
                color={
                  currentReview.decision === 'approve'
                    ? 'success'
                    : currentReview.decision === 'reject'
                    ? 'error'
                    : 'warning'
                }
                size="small"
              />
            )}
            <Typography variant="caption" color="text.secondary">
              /{slug}
            </Typography>
          </Box>
        </Box>
        {hasPermission('content', 'edit') && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/guides/${slug}/edit`)}
            sx={{ mr: 1 }}
          >
            Edit guide
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<OpenIcon />}
          href={guidePreviewUrl(slug || '')}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open preview
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          {/* Publish gate checklist (G3) */}
          <Paper sx={{ p: 3, mb: 2 }} data-testid="publish-gates">
            <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
              Publish checklist
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              A guide goes live only when every required check passes and a named reviewer has
              approved this exact copy. Advisory checks do not block.
            </Typography>
            {blockedGates && (
              <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setBlockedGates(null)}>
                Publish was blocked:
                <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                  {blockedGates.map((g) => (
                    <li key={g.gate}>
                      {g.gate}: {g.detail}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}
            {gates ? (
              <List dense disablePadding>
                {gates.gates.map((g, idx) => (
                  <Box key={g.gate}>
                    <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                      <Chip
                        label={GATE_STATUS_LABELS[g.status]}
                        color={GATE_STATUS_COLORS[g.status]}
                        size="small"
                        variant={g.status === 'advisory' ? 'outlined' : 'filled'}
                        sx={{ mr: 1.5, mt: 0.25, minWidth: 74 }}
                      />
                      <ListItemText
                        primary={g.label}
                        secondary={g.detail}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                    {g.gate === 'fact_check' && gates.factCheck && (
                      <GuideFactCheckFindings
                        slug={guide.slug || slug || ''}
                        factCheck={gates.factCheck}
                        isReviewer={myReviewer !== null}
                        onChanged={() => fetchGates(confirmSuperlative)}
                      />
                    )}
                    {idx < gates.gates.length - 1 && <Divider component="li" />}
                  </Box>
                ))}
              </List>
            ) : (
              <Alert severity="warning">
                {gatesError || 'The publish checklist is not available.'}
              </Alert>
            )}
          </Paper>

          {/* Deterministic QA */}
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
              Deterministic QA
            </Typography>
            {qaReport ? (
              <>
                <Chip
                  label={qaReport.ok ? 'Pass' : 'Fail'}
                  color={qaReport.ok ? 'success' : 'error'}
                  size="small"
                  sx={{ mb: 1.5 }}
                />
                {qaReport.picks !== undefined && (
                  <Typography variant="body2" color="text.secondary">
                    Picks: {qaReport.picks} · Imaged: {qaReport.imaged ?? '—'}
                  </Typography>
                )}
                {qaReport.failures && qaReport.failures.length > 0 && (
                  <List dense>
                    {qaReport.failures.map((failure, idx) => (
                      <ListItem key={idx} sx={{ py: 0.25 }}>
                        <ListItemText
                          primary={failure}
                          primaryTypographyProps={{ variant: 'body2', color: 'error.main' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No QA report on this guide yet.
              </Typography>
            )}
          </Paper>

          {/* Judge */}
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
              Editor's Read (Judge)
            </Typography>
            {judge ? (
              <>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
                  {judge.verdict && (
                    <Chip
                      label={judge.verdict}
                      color={VERDICT_COLORS[judge.verdict] || 'default'}
                      size="small"
                    />
                  )}
                  {judge.score !== undefined && judge.score !== null && (
                    <Typography variant="body2" color="text.secondary">
                      Score: {judge.score}/10
                    </Typography>
                  )}
                </Box>
                {judge.one_line && (
                  <Typography variant="body1" sx={{ mb: 1, fontStyle: 'italic' }}>
                    "{judge.one_line}"
                  </Typography>
                )}
                {judge.the_tell && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    The tell: {judge.the_tell}
                  </Typography>
                )}
                {judge.findings && judge.findings.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                    {judge.findings.map((finding) => (
                      <Chip key={finding} label={finding} size="small" variant="outlined" />
                    ))}
                  </Box>
                )}
                {judge.what_fails && judge.what_fails.length > 0 && (
                  <List dense>
                    {judge.what_fails.map((item, idx) => (
                      <ListItem key={idx} sx={{ py: 0.25 }}>
                        <ListItemText
                          primary={item}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Not judged yet.
              </Typography>
            )}
          </Paper>

          {/* Review history */}
          {history.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
                Review History
              </Typography>
              <List dense>
                {history.map((entry, idx) => (
                  <Box key={idx}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip label={entry.decision || entry.action} size="small" />
                            <Typography variant="body2">{entry.reviewer}</Typography>
                            {(entry.at || entry.timestamp) && (
                              <Typography variant="caption" color="text.secondary">
                                {entry.at || entry.timestamp}
                              </Typography>
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            {(entry.reasons?.length ?? 0) > 0 && (
                              <Typography variant="caption" component="div">
                                Reasons: {entry.reasons?.join(', ')}
                              </Typography>
                            )}
                            {entry.note && (
                              <Typography variant="caption" component="div">
                                Note: {entry.note}
                              </Typography>
                            )}
                            {entry.action === 'publish' && entry.approvedBy && (
                              <Typography variant="caption" component="div">
                                Approved by {entry.approvedBy}, published by{' '}
                                {entry.publishedBy || entry.reviewer}
                              </Typography>
                            )}
                            {entry.reason && (
                              <Typography variant="caption" component="div">
                                Reason: {entry.reason}
                              </Typography>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                    {idx < history.length - 1 && <Divider />}
                  </Box>
                ))}
              </List>
            </Paper>
          )}
        </Grid>

        {/* Actions */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                Actions
              </Typography>
              {!canReview ? (
                <Typography variant="body2" color="text.secondary">
                  You do not have permission to review guides.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<ApproveIcon />}
                    onClick={handleApprove}
                    disabled={actionLoading}
                    fullWidth
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<RequestChangesIcon />}
                    onClick={() => openDialog('request_changes')}
                    disabled={actionLoading}
                    fullWidth
                  >
                    Request Changes
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<RejectIcon />}
                    onClick={() => openDialog('reject')}
                    disabled={actionLoading}
                    fullWidth
                  >
                    Reject
                  </Button>
                  {actionLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Publish / Unpublish (G3) */}
          {canReview && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                  {isPublished ? 'Published' : 'Publish'}
                </Typography>
                {myReviewer && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="div"
                    sx={{ mb: 1 }}
                  >
                    Publishing as {myReviewer}
                  </Typography>
                )}
                {isPublished ? (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<UnpublishIcon />}
                    onClick={() => {
                      setUnpublishReason('');
                      setUnpublishOpen(true);
                    }}
                    disabled={actionLoading || !myReviewer}
                    fullWidth
                  >
                    Unpublish
                  </Button>
                ) : (
                  <>
                    {gates?.superlativeTitle && (
                      <FormControlLabel
                        sx={{ mb: 1, alignItems: 'flex-start' }}
                        control={
                          <Checkbox
                            checked={confirmSuperlative}
                            onChange={(e) => toggleSuperlative(e.target.checked)}
                            disabled={actionLoading}
                          />
                        }
                        label="I've reviewed this guide and it earns 'Best'"
                      />
                    )}
                    <Button
                      variant="contained"
                      startIcon={<PublishIcon />}
                      onClick={() => setPublishOpen(true)}
                      disabled={actionLoading || publishBlocker !== null}
                      fullWidth
                    >
                      Publish
                    </Button>
                    {publishBlocker && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="div"
                        sx={{ mt: 1 }}
                        data-testid="publish-blocker"
                      >
                        {publishBlocker}
                      </Typography>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Reject / Request Changes Dialog */}
      <Dialog open={dialogDecision !== null} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogDecision === 'reject' ? 'Reject Guide' : 'Request Changes'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select every reason that applies. This guide stays in draft either way — the
            reasons and note are what the pipeline (and the next judge run) learn from.
          </Typography>
          <FormGroup sx={{ mb: 2 }}>
            {GUIDE_REVIEW_REASONS.map((reason) => (
              <FormControlLabel
                key={reason}
                control={
                  <Checkbox
                    checked={selectedReasons.includes(reason)}
                    onChange={() => toggleReason(reason)}
                  />
                }
                label={GUIDE_REVIEW_REASON_LABELS[reason]}
              />
            ))}
          </FormGroup>
          <TextField
            label={requiresNote ? 'Note (required for "Other")' : 'Note (optional)'}
            multiline
            rows={3}
            fullWidth
            value={note}
            onChange={(e) => setNote(e.target.value)}
            error={requiresNote && note.trim().length === 0}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={dialogDecision === 'reject' ? 'error' : 'warning'}
            onClick={handleDialogSubmit}
            disabled={actionLoading || !canSubmitDialog}
          >
            {dialogDecision === 'reject' ? 'Reject' : 'Request Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish confirmation (G3) */}
      <Dialog
        open={publishOpen}
        onClose={() => !actionLoading && setPublishOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Publish this guide?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            "{title}" goes live at /destinations/{slug} and is recorded as published by{' '}
            {myReviewer}. You can unpublish it afterwards, but search engines may already have
            seen it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handlePublish} disabled={actionLoading}>
            Publish
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unpublish (G3) */}
      <Dialog
        open={unpublishOpen}
        onClose={() => !actionLoading && setUnpublishOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Unpublish this guide</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The guide returns to draft and its page is refreshed. The reason is kept in the review
            history.
          </Typography>
          <TextField
            label="Reason (required)"
            multiline
            rows={3}
            fullWidth
            value={unpublishReason}
            onChange={(e) => setUnpublishReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnpublishOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleUnpublish}
            disabled={actionLoading || unpublishReason.trim().length === 0}
          >
            Unpublish
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
